import type { EventTypeAttachment, SearchEndpointResponse } from '@ps/isp-web-services-types';
import type { Alias } from '@ps/web-core-store';

import { initialSearchResults } from '../model/defaultState';
import type { SearchLinks, SearchMatch, SearchMatches, SearchResults } from '../model/types';

export function getLinksMatchingQuery(searchQuery: string, links: SearchLinks) {
  return links.filter((link) => {
    if ('text' in link) {
      return link.text.toLowerCase().includes(searchQuery);
    }

    if ('runnerName' in link) {
      return link.runnerName.toLowerCase().includes(searchQuery);
    }

    if ('venue' in link) {
      return link.venue.toLowerCase().includes(searchQuery);
    }

    return false;
  });
}

export function mapLinkToSearchMatch(link: SearchLinks[number]): SearchMatch {
  const isLinkEventOrCompetition = 'type' in link;
  let alias: Alias = 'sportsAnimalRacingRace';

  if (isLinkEventOrCompetition) {
    alias = link.type === 'EVENT' ? 'sportsEvent' : 'sportsCompetition';
  }

  return {
    alias,
    link
  };
}

export function getUniqueEventTypes(
  links: SearchLinks,
  eventTypes: Partial<SearchEndpointResponse['eventTypes']>
) {
  const seenEventTypeIds = new Set();
  const uniqueEventTypes: EventTypeAttachment[] = [];

  for (const { eventTypeId } of links) {
    if (!seenEventTypeIds.has(eventTypeId)) {
      seenEventTypeIds.add(eventTypeId);
      const eventType = eventTypes[eventTypeId];
      if (eventType) {
        uniqueEventTypes.push(eventType);
      }
    }
  }
  return uniqueEventTypes;
}

export const getSearchResults = (
  searchQuery: string,
  { eventsAndRunners, competitionsAndMeetings, eventTypes }: SearchEndpointResponse
): SearchResults => {
  const combinedLinks: SearchLinks = [...eventsAndRunners, ...competitionsAndMeetings];

  const linksByQuery = getLinksMatchingQuery(searchQuery, combinedLinks);

  if (!linksByQuery.length) {
    return initialSearchResults;
  }

  const linksUniqueEventTypes = getUniqueEventTypes(linksByQuery, eventTypes);
  const linksToSearchMatches = linksByQuery.reduce<SearchMatches>(
    (searchMatches, link) => {
      const searchMatch = mapLinkToSearchMatch(link);

      switch (true) {
        case searchMatch.alias === 'sportsEvent':
          searchMatches.events.push(searchMatch);
          break;
        case searchMatch.alias === 'sportsCompetition':
          searchMatches.competitions.push(searchMatch);
          break;
        case 'runnerName' in link:
          searchMatches.runners.push(searchMatch);
          break;
        case 'venue' in link:
          searchMatches.meetings.push(searchMatch);
          break;
      }

      return searchMatches;
    },
    { events: [], competitions: [], runners: [], meetings: [] }
  );

  return {
    uniqueEventTypes: linksUniqueEventTypes,
    matches: linksToSearchMatches
  };
};
